import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Brain, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/kit";
import { apiErr } from "@/lib/api";
import IsaChatMockup from "@/components/IsaChatMockup";
import AuthBackground from "@/components/AuthBackground";

const PILLS = [
  { icon: Brain, label: "Memória viva" },
  { icon: MessageSquare, label: "Opera no WhatsApp" },
  { icon: ShieldCheck, label: "Governança total" },
];

export default function Auth() {
  const { t } = useI18n();
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", company_name: "", admin_name: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(f.email, f.password);
      else await register({ company_name: f.company_name, admin_name: f.admin_name, email: f.email, password: f.password });
      toast.success("Bem-vindo à ASIISA");
      nav("/app");
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#070b16] px-4 py-10">
      {/* animated dark background: aurora + neural particles + live story cards */}
      <AuthBackground />

      {/* brand + footer */}
      <img src="/asiisa-logo.png" alt="ASIISA" className="absolute left-8 top-7 z-20 h-6 w-auto brightness-0 invert" />
      <p className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2 text-xs tracking-wide text-white/35">asiisa.com.br — © {new Date().getFullYear()} ASIISA</p>

      {/* floating glass panel */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 grid w-full max-w-5xl grid-cols-1 items-center gap-8 overflow-hidden rounded-[2.25rem] border border-white/70 bg-white/95 p-6 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.75)] backdrop-blur-2xl sm:p-10 lg:grid-cols-2 lg:gap-4"
      >
        {/* form side */}
        <div className="mx-auto w-full max-w-sm">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/60 px-3 py-1 text-xs font-medium text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Plataforma operacional com IA
          </span>
          <h1 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-[#0b1220] sm:text-4xl">
            {mode === "login" ? "Bem-vindo de volta." : "Crie sua conta."}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            {mode === "login" ? "Acesse o console operacional da ASIISA." : "Comece a operar sua empresa com inteligência."}
          </p>

          <div className="mt-7 mb-5 inline-flex w-full gap-1 rounded-full border border-slate-200 bg-slate-50 p-1 text-sm">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`tab-${m}`} type="button" onClick={() => setMode(m)}
                className={`flex-1 rounded-full py-2 font-medium transition-[background-color,color] duration-200 ${mode === m ? "bg-[#0b1220] text-white shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>
                {t(m)}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <Input data-testid="input-company" placeholder={t("company")} value={f.company_name} onChange={set("company_name")} required />
                <Input data-testid="input-adminname" placeholder={t("your_name")} value={f.admin_name} onChange={set("admin_name")} required />
              </>
            )}
            <Input data-testid="input-email" type="email" placeholder={t("email")} value={f.email} onChange={set("email")} required />
            <Input data-testid="input-password" type="password" placeholder={t("password")} value={f.password} onChange={set("password")} required />
            <button data-testid="submit-auth" type="submit" disabled={busy}
              className="group mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0b1220] px-5 py-3 text-sm font-semibold text-white transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#1a2438] hover:shadow-xl hover:shadow-slate-900/15 active:scale-[.99] disabled:opacity-50">
              {busy ? "..." : t(mode)}
              {!busy && <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />}
            </button>
          </form>

          <div className="mt-6 flex flex-wrap gap-2">
            {PILLS.map((p) => (
              <span key={p.label} className="inline-flex items-center gap-1.5 rounded-full bg-slate-900/[0.04] px-3 py-1 text-xs font-medium text-slate-600">
                <p.icon size={13} strokeWidth={1.75} className="text-[#0b1220]" /> {p.label}
              </span>
            ))}
          </div>
        </div>

        {/* mockup side (decorative) */}
        <div className="hidden items-center justify-center lg:flex">
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: 3 }}
            animate={{ opacity: 1, y: 0, rotate: 3 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <IsaChatMockup />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
